import type { IKeyedGroups } from "helpers/collections";
import type { RequestStatus } from "types/request";

export interface IGroupManagementState {
  menus: IKeyedGroups<IMenuRecord>;
  sections: IKeyedGroups<ISectionRecord>;
  items: IKeyedGroups<IItemRecord>;
  categories: IKeyedGroups<ICategory>;
  courses: IKeyedGroups<ICourse>;
  // Standalone modifier records (new summary key). A store id that is
  // ABSENT here means "modifier data unknown" (old pos-service deploy or
  // legacy read fallback, which never fetches modifiers) — never "zero
  // modifiers".
  modifiers: IKeyedGroups<IModifierRecord>;
  modifierGroups: IKeyedGroups<IModGroupRecord>;
  printerGroups: IKeyedGroups<IPrinterGroup>;
  taxRates: IKeyedGroups<ITaxRate>;
  requestStatus: {
    [index: string]: GroupManagementRequests;
  };
}

export type StoreIdList = string[];

export interface IPromisesByStoreId<T> {
  [index: string]: Promise<T>[];
}

export enum GroupManagementRequestKeys {
  GetMenus = "GetMenus",
  GetSections = "GetSections",
  GetMenuComponents = "GetMenuComponents",
  PostItem = "PostItem",
  PutItem = "PutItem",
  PostMenu = "PostMenu",
  PutMenu = "PutMenu",
  DeleteMenu = "DeleteMenu",
  PostOrPutItem = "PostOrPutItem",
  PostCategory = "PostCategory",
  PutCategory = "PutCategory",
  DeleteCategory = "DeleteCategory",
  PostOrPutCategory = "PostOrPutCategory",
  PostCourse = "PostCourse",
  PutCourse = "PutCourse",
  PostOrPutCourse = "PostOrPutCourse",
  PostModifier = "PostModifier",
  PutModifier = "PutModifier",
  PostOrPutModifier = "PostOrPutModifier",
  PostModifierGroup = "PostModifierGroup",
  PutModifierGroup = "PutModifierGroup",
  PostOrPutModifierGroup = "PostOrPutModifierGroup",
  PostSection = "PostSection",
  PutSection = "PutSection",
  DeleteSection = "DeleteSection",
  PostOrPutSection = "PostOrPutSection",
}

export type GroupManagementRequests = {
  [key in GroupManagementRequestKeys]: RequestStatus;
};

// Hydrated interfaces that extend record returned from the backend.
// Hydration occurs in selectors/groupManagement.

export interface IHQKeyInfo {
  storeId: string;
  storeName: string;
}

// Hydrated Menu interface includes the actual ISection records, the storeId,
// and sums up the items count.
export interface IMenu extends IMenuRecord, IHQKeyInfo {
  sections: ISection[];
  itemsCount: number;
}

// Hydrated interfaces includes storeId, storeName
export interface IItem extends IItemRecord, IHQKeyInfo {}
export interface ISection extends ISectionRecord, IHQKeyInfo {}
export interface IModGroup extends IModGroupRecord, IHQKeyInfo {}
export interface IModifier extends IModifierRecord, IHQKeyInfo {}
export interface ICategoryHydrated extends ICategory, IHQKeyInfo {}
export interface ICourseHydrated extends ICourse, IHQKeyInfo {}

// Record from the API, has references to child sections
export interface IMenuRecord {
  id: string;
  name: string;
  updatedAt: string;
  restaurantId: string;
  menuSections: ISectionReference[];
}

// When saving a Menu, pass field `menuSectionsAttributes` rather than menuSections
export interface IMenuSaveRecord extends Omit<IMenuRecord, "menuSections"> {
  menuSectionsAttributes: ISectionReference[];
}

// When saving an Item, pass ____Attributes fields. Omit the corresponding fields from IItemRecord
export interface IItemSaveRecord
  extends Omit<
    IItemRecord,
    | "itemModifierGroups"
    | "modifierGroups"
    | "restaurantId"
    | "sections"
    | "sides"
    | "taxRates"
  > {
  taxRatesAttributes: { taxRateId: string }[];
  sectionsAttributes: { sectionId: string; order: number }[];
  modifierGroupsAttributes: { modifierGroupId: string; order: number }[];
  sidesAttributes: Omit<ISide, "updatedAt">[];
}

export interface ISectionReference {
  id: string;
  order: number;
  sectionId: string;
  updatedAt: string;
  menuId: string;
}

export interface ISectionRecord {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  restaurantId: string;
  itemsCount: number;
  sections: ISectionRecord[];
  timingMask?: ITimingMask;
  // Emitted by Hq::SectionSerializer. Only user-editable (STANDARD) display
  // groups may be renamed, re-membered, or deleted; system/OLO ones render
  // read-only.
  sectionType?: number;
  userEditable?: boolean;
  // Item membership with join ids and order (summary + save responses only;
  // absent from legacy sections.json payloads).
  sectionItems?: ISectionItemRecord[];
  // Sub-display-group membership: ordered links to nested sections (summary +
  // save responses only; via GroupManagement::SectionSerializer). `sectionId`
  // is the linked (sub) section's id (Hq::SectionLinkSerializer's section_id =
  // section_to_id).
  sectionLinks?: ISectionLinkRecord[];
  // Whether the restaurant is entitled to timed display groups
  // (TIMED_DISPLAY_GROUPS). Emitted by GroupManagement::SectionSerializer on
  // the summary + save responses. The Display Group editor hides the timing
  // control unless every targeted location has it — otherwise a saved schedule
  // is silently dropped (the pos save/read both feature-gate timing_mask).
  timedDisplayGroupsEnabled?: boolean;
}

export interface ISectionItemRecord {
  id: string;
  sectionId: string;
  itemId: string;
  order: number;
}

export interface ISectionLinkRecord {
  // The linked (sub) section's id.
  sectionId: string;
  order: number;
}

// When saving a Display Group (section), item membership rides beside the
// section payload in the operation, not nested in it.
export interface ISectionSaveRecord {
  id?: string;
  name: string;
}

export interface ISectionItemAttribute {
  id?: string;
  itemId: string;
  order: number;
}

export interface ITimingMask {
  id: string;
  createdAt: string;
  startTime: string;
  endTime: string;
  startDate: string | null;
  endDate: string | null;
  rules: string[];
  status: string;
  ownerId: string;
}

export interface IMenuComponents {
  items: IItemRecord[];
  categories: ICategory[];
  courses: ICourse[];
  modifierGroups: IModGroupRecord[];
  printerGroups: IPrinterGroup[];
  taxRates: ITaxRate[];
  // Present only in newer summary payloads (never in the legacy
  // multi-location components response); the reducer skips absent keys.
  modifiers?: IModifierRecord[];
}

export interface IItemRecord {
  id: string;
  name: string;
  allowPriceOverride: boolean;
  categoryId: string;
  chitName: string;
  // Online-ordering / alcohol fields, read via GroupManagement::ItemSerializer
  // (absent from the shared mossy serializer). containsAlcohol is a Universal
  // field; oloPrice is per-location (null = "same as POS price").
  containsAlcohol: boolean;
  cost: string;
  courseId: string;
  defaultOnDelivery: boolean;
  defaultOnTab: boolean;
  defaultOnTable: boolean;
  defaultOnToGo: boolean;
  description: string | null;
  feePercentage: string | null;
  fireImmediately: boolean;
  itemIdentifier: string | null;
  itemImages: IItemImage[];
  itemModifierGroups: IItemModGroupRecord[];
  itemType: number;
  maxSides: number;
  minSides: number;
  modifierGroups: string[];
  noPrint: boolean;
  oloPrice: string | null;
  onlineMenuDescription: string | null;
  onlineMenuName: string | null;
  price: string;
  receiptName: string;
  resetStockLevelTo: number | null;
  resetStockOnClose: number;
  restaurantId: string;
  sections: string[];
  sides?: ISide[];
  sortCode: string;
  stationId: string | null;
  status: number;
  stockLevel: number | null;
  suppressFromCustomerReceipt: boolean;
  suppressServiceFees: boolean;
  taxRates: string[];
  trackPreOrderedStock: boolean;
  updatedAt: string;
}

export interface IItemImage {
  id: string;
  itemId: string;
  userId: string;
  metadata: unknown;
  usedAs: number;
  thumbUrl: string;
}

export interface ISide {
  // This field refers to the id of the join record itself
  id: string;
  price: string;
  status: number;
  updatedAt: string;
  // Due to a quirk in pos-service serialization, Sides come through
  // with differing fields during GET vs POST operations.
  itemId?: string; // item id of the parent item
  sideId?: string; // item id of the child item
}

export interface ICategory {
  id: string;
  name: string;
  defaultTaxType: number;
  parentId: string;
  requiresPosition: boolean;
  resetStockOnClose: number;
  stationId: string;
  status: number;
  updatedAt: string;
}

// When saving a Category, id is the lookup key (create ops omit it).
export type ICategorySaveRecord = Omit<ICategory, "updatedAt">;

export interface ICourse {
  id: string;
  name: string;
  autoFire: boolean;
  isPeripheral: boolean;
  order: number | null;
  restaurantId: string;
  status: number;
  updatedAt: string;
}

export type ICourseSaveRecord = Omit<ICourse, "updatedAt" | "restaurantId">;

// Standalone modifier record (summary `modifiers` key; pos-service
// GroupManagement::ModifierWithTaxRatesSerializer).
export interface IModifierRecord {
  id: string;
  name: string;
  chitName: string | null;
  description: string | null;
  price: string;
  receiptName: string | null;
  restaurantId: string;
  sortCode?: string | null;
  status: number;
  // Legacy single-tax scalar; read-only (all merchants are multi-tax).
  taxRateId: string | null;
  // Tax-rate membership ids (modifiers_tax_rates) — where tax actually
  // lives. Optional: absent on payloads from older deploys.
  taxRates?: string[];
  updatedAt: string;
}

// Tax rides as multiTaxAttributes (the COMPLETE membership — the backend
// reconciles the full list, injecting the zero rate on priced modifiers when
// empty). Omitted entirely when membership is unknown (old payloads): the
// backend leaves tax untouched for an absent key.
export interface IModifierSaveRecord
  extends Omit<
    IModifierRecord,
    "updatedAt" | "restaurantId" | "taxRateId" | "taxRates"
  > {
  multiTaxAttributes?: { taxRateId: string }[];
}

export interface IModGroupRecord {
  id: string;
  name: string;
  maximumRequired: number;
  // OLO visibility ("show modifiers to online-ordering customers"), read via
  // GroupManagement::ModifierGroupSerializer. 1 = always visible,
  // 2 = hidden from online menu (pos Enums::ModifierGroup::MenuDisplayStatus).
  menuDisplayStatus: number;
  minimumRequired: number;
  modifiers: string[];
  nameLong: string;
  printName: boolean;
  restaurantId: string;
  sortCode?: string | null;
  status: number;
  updatedAt: string;
}

// When saving a Modifier Group, modifier membership + join order ride as
// modifiersAttributes. The COMPLETE membership must always be sent: the
// backend reconciles the full list (an omitted or empty list removes every
// modifier from the group).
export interface IModGroupSaveRecord
  extends Omit<IModGroupRecord, "updatedAt" | "restaurantId" | "modifiers"> {
  modifiersAttributes: { id: string; order: number }[];
}

// item-mod group join record
export interface IItemModGroupRecord {
  id: string;
  itemId: string;
  modifierGroupId: string;
  order: number;
}

export interface IOrderedModGroup extends IModGroup {
  order: number;
}

export interface IPrinterGroup {
  id: string;
  name: string;
  kitchenDisplaySystems: string[];
  printCourselines: boolean;
  printers: string[];
  restaurantId: string;
  status: number;
  updatedAt: string;
}

export interface ITaxRate {
  id: string;
  name: string;
  displayPercent: string;
  rate: string;
  status: number;
  updatedAt: string;
}

// Counted tax rates are used on Item Create page.
// built in selector.
// The selector merges the whole rate in, so name / displayPercent are available
// — the picker shows them as the Figma's separate "Name" and "Rate" columns.
export interface ITaxRateCounted extends ITaxRate {
  label: string;
  count: number;
}

export enum ItemReviewChangeType {
  ADD = "add",
  REMOVE = "remove",
  EDIT = "edit",
}

export interface IFieldOrderMap {
  field: keyof IItem;
  label: string;
}

export interface IChange {
  key: keyof IItem | "itemAdded";
  label: string;
  storeName: string;
  storeId: string;
  changes?: string;
  type: ItemReviewChangeType;
}
